import { useState } from 'react';
import { toast } from 'sonner';
import { FileText, Image as ImageIcon, Loader2, Sparkles, AlertTriangle, PauseCircle, Clock, Copy, FolderX, MessageSquareWarning, Check, Landmark, Pencil, Mail, Scissors, FileOutput } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { fmtGBP } from './LineItemsTable';
import { STATUS_BADGE, STATUS_LABEL, OPEN_STATUSES, isReading, isReadTimedOut, fmtUKDateTime, fmtUKDate } from './inboxFormat';
import { LiveCostsStatus, XeroStatus } from './LiveCostsActions';
import { DeleteInvoiceButton, RestoreButton, PurgeInvoiceButton } from './InboxActions';

function DuplicateBadge({ it, api, onRefresh }) {
  const dups = it.duplicates || [];
  const [busy, setBusy] = useState(false);
  if (!dups.length) return null;
  const dismiss = async (otherId) => {
    setBusy(true);
    try {
      await api.post(`/cost-inbox/${it.id}/dismiss-duplicate`, { other_id: otherId });
      toast.success('Marked "not a duplicate"');
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update');
    } finally { setBusy(false); }
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`inbox-dup-badge-${it.id}`}
          className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/40 hover:bg-amber-500/25 transition-colors shrink-0">
          <Copy className="w-3 h-3" /> Possible duplicate{dups.length > 1 ? ` (${dups.length})` : ''}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 bg-[#2A3240] border-white/10 text-white p-3" data-testid={`inbox-dup-panel-${it.id}`}>
        <div className="text-sm font-medium mb-0.5">Looks like the same invoice</div>
        <div className="text-xs text-gray-400 mb-3">Matches on supplier, invoice number and value:</div>
        <div className="space-y-2 max-h-72 overflow-auto">
          {dups.map(d => (
            <div key={d.id} className="rounded border border-white/10 p-2 text-xs" data-testid={`inbox-dup-match-${it.id}-${d.id}`}>
              <div className="font-medium text-gray-100 truncate">{d.supplier_name || d.original_filename || 'invoice'}</div>
              <div className="text-gray-400">Ref {d.reference || '—'} · {typeof d.value_pence === 'number' ? fmtGBP(d.value_pence) : '—'}{d.invoice_date ? ` · ${fmtUKDate(d.invoice_date)}` : ''}</div>
              <div className="mt-1.5 flex items-center justify-between gap-2">
                <span className={`px-1.5 py-0.5 rounded-full ${d.posted ? 'bg-emerald-500/15 text-emerald-300' : 'bg-white/10 text-gray-300'}`}>{d.posted ? 'Already posted' : 'Waiting in inbox'}</span>
                <button type="button" disabled={busy} onClick={() => dismiss(d.id)} data-testid={`inbox-dup-dismiss-${it.id}-${d.id}`} className="text-gray-400 hover:text-white underline disabled:opacity-50">Not a duplicate</button>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function QueryBadge({ it, api, onRefresh }) {
  const q = it.query;
  const [busy, setBusy] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [note, setNote] = useState('');
  if (!q || q.status !== 'open') return null;
  const resolve = async () => {
    setBusy(true);
    try {
      await api.post(`/cost-inbox/${it.id}/query/resolve`, { note });
      toast.success('Query marked dealt with');
      onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); }
    finally { setBusy(false); }
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`inbox-query-badge-${it.id}`}
          className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/40 hover:bg-amber-500/25 transition-colors shrink-0">
          <MessageSquareWarning className="w-3 h-3" /> Query
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 bg-[#2A3240] border-white/10 text-white p-3" data-testid={`inbox-query-panel-${it.id}`}>
        <div className="text-sm font-medium mb-0.5 text-amber-200">Supplier query</div>
        <div className="text-xs text-gray-200 whitespace-pre-wrap break-words mb-1" data-testid={`inbox-query-note-${it.id}`}>{q.note}</div>
        <div className="text-[11px] text-gray-400 mb-3">Flagged {fmtUKDateTime(q.flagged_at)}{q.flagged_by_name ? ` by ${q.flagged_by_name}` : ''}</div>
        {!resolving ? (
          <Button size="sm" onClick={() => setResolving(true)} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`inbox-query-resolve-${it.id}`}>
            <Check className="w-3.5 h-3.5 mr-1" /> Mark dealt with
          </Button>
        ) : (
          <div className="space-y-2">
            <Textarea value={note} onChange={e => setNote(e.target.value)} className="bg-[#1E2530] border-white/20 text-white min-h-[56px] text-sm" placeholder="Outcome (optional)" data-testid={`inbox-query-res-note-${it.id}`} />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setResolving(false)}>Cancel</Button>
              <Button size="sm" onClick={resolve} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`inbox-query-res-confirm-${it.id}`}>
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Confirm'}
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}


function ReadState({ it }) {
  if (isReading(it)) return <span className="inline-flex items-center gap-1 text-xs text-[#F5A623]" data-testid={`inbox-read-state-${it.id}`}><Loader2 className="w-3.5 h-3.5 animate-spin" /> AI reading…</span>;
  if (isReadTimedOut(it)) return <span className="inline-flex items-center gap-1 text-xs text-amber-300" title="The AI read is taking longer than expected — open to retry or key it in" data-testid={`inbox-read-state-${it.id}`}><Clock className="w-3.5 h-3.5" /> Read timed out</span>;
  if (it.extraction_status === 'failed') return <span className="inline-flex items-center gap-1 text-xs text-red-300" title={it.extraction_error || ''} data-testid={`inbox-read-state-${it.id}`}><AlertTriangle className="w-3.5 h-3.5" /> Read failed</span>;
  if (it.extraction_status === 'done' && it.status !== 'posted') return <span className="inline-flex items-center gap-1 text-xs text-emerald-300" data-testid={`inbox-read-state-${it.id}`}><Sparkles className="w-3.5 h-3.5" /> Read · {it.lines?.length || 0} line{(it.lines?.length || 0) === 1 ? '' : 's'}</span>;
  return null;
}

export function InboxRow({ it, onOpen, api, onDeleted, onRestore, onPurge, onRefresh }) {
  const open = OPEN_STATUSES.includes(it.status);
  const refs = [...new Set((it.lines || []).map(l => l.project_ref).filter(Boolean))];
  return (
    <div className="bg-[#2A3240] border border-white/10 rounded-lg px-4 py-3 flex flex-wrap items-center gap-4 hover:border-white/20 transition-colors" data-testid={`inbox-item-${it.id}`}>
      <div className="w-9 h-9 rounded bg-white/5 flex items-center justify-center shrink-0">
        {it.content_type === 'application/pdf' ? <FileText className="w-5 h-5 text-gray-300" /> : <ImageIcon className="w-5 h-5 text-gray-300" />}
      </div>
      <div className="flex-1 min-w-[220px]">
        <div className="font-medium truncate flex items-center gap-2">
          <span className="truncate">{it.supplier_name || it.original_filename || 'invoice'}</span>
          {it.supplier_name && <span className="text-xs text-gray-500 truncate hidden md:inline">{it.original_filename}</span>}
        </div>
        <div className="text-xs text-gray-400 flex flex-wrap items-center gap-x-2">
          <span>{fmtUKDateTime(it.created_at)}</span>
          {it.reference && <span>· Ref {it.reference}</span>}
          {it.invoice_date && <span>· Dated {fmtUKDate(it.invoice_date)}</span>}
          {refs.length > 0 && <span className="text-gray-300">· {refs.length > 2 ? `${refs.length} projects` : refs.join(', ')}</span>}
          {it.page_count > 1 && <span>· {it.page_count} pages</span>}
        </div>
      </div>
      <ReadState it={it} />
      {it.source === 'email' && (
        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-200 border border-sky-400/40 shrink-0" title={`Emailed in by ${it.email_in?.from_name || it.email_in?.from || 'unknown'}${it.email_in?.subject ? ` · "${it.email_in.subject}"` : ''}`} data-testid={`inbox-email-badge-${it.id}`}>
          <Mail className="w-3 h-3" /> Email
        </span>
      )}
      {it.cis?.enabled && (
        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#F5A623]/15 text-[#F5A623] border border-[#F5A623]/40 shrink-0" title={`CIS invoice · ${Number(it.cis.rate) === 0 ? 'gross status' : `${Number(it.cis.rate)}% deduction`}`} data-testid={`inbox-cis-badge-${it.id}`}>
          <Landmark className="w-3 h-3" /> CIS {Number(it.cis.rate) === 0 ? 'gross' : `${Number(it.cis.rate)}%`}
        </span>
      )}
      {it.split && (
        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border shrink-0 ${it.split.part === 'CON' ? 'bg-sky-500/10 text-sky-200 border-sky-400/40' : 'bg-[#F5A623]/10 text-[#F5A623] border-[#F5A623]/40'}`} title={`Split copy ${it.split.part_no} of 2 — the ${it.split.part === 'CON' ? 'LUX Construction' : 'LUX Developments'} share of the supplier's ${fmtGBP(it.split.parent_net_pence || 0)}`} data-testid={`inbox-split-badge-${it.id}`}>
          <Scissors className="w-3 h-3" /> {it.split.part === 'CON' ? 'Construction' : 'Developments'} share
        </span>
      )}
      {it.status === 'split' && (
        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-200 border border-sky-400/40 shrink-0" title="Split into one copy per company — post each copy" data-testid={`inbox-split-parent-badge-${it.id}`}>
          <Scissors className="w-3 h-3" /> Split into 2
        </span>
      )}
      {it.send_as === 'generated' && !it.split && (
        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-white/5 text-gray-300 border border-white/15 shrink-0" title="Sent to Xero / LiveCosts as a generated invoice; the original is kept here" data-testid={`inbox-generated-badge-${it.id}`}>
          <FileOutput className="w-3 h-3" /> Generated
        </span>
      )}
      {!it.deleted && <DuplicateBadge it={it} api={api} onRefresh={onRefresh} />}
      {!it.deleted && <QueryBadge it={it} api={api} onRefresh={onRefresh} />}
      <LiveCostsStatus item={it} compact />
      <XeroStatus item={it} compact />
      {it.no_project && (
        <span className="text-xs text-amber-300 inline-flex items-center gap-1 shrink-0" title="Tagged: no project assigned" data-testid={`inbox-no-project-${it.id}`}>
          <FolderX className="w-3.5 h-3.5" /> No project
        </span>
      )}
      {it.status === 'on_hold' && it.hold_note && <span className="text-xs text-purple-300 inline-flex items-center gap-1 max-w-[220px] truncate" title={it.hold_note}><PauseCircle className="w-3.5 h-3.5 shrink-0" /> {it.hold_note}</span>}
      <div className="w-28 text-right" data-testid={`inbox-item-total-${it.id}`}>
        <div className="text-sm text-gray-200 tabular-nums">{(it.net_pence ?? it.value_pence) ? fmtGBP(it.net_pence ?? it.value_pence) : '—'}</div>
        {it.vat_pence > 0 && <div className="text-[10px] text-gray-500 tabular-nums">ex VAT · {fmtGBP(it.value_pence)} inc</div>}
      </div>
      <Badge variant="outline" className={STATUS_BADGE[it.status]}>{STATUS_LABEL[it.status] || it.status}</Badge>
      {it.deleted ? (
        <>
          <span className="text-xs text-gray-400" title={`Deleted by ${it.deleted_by_name || 'admin'}${it.deleted_reason ? ` — ${it.deleted_reason}` : ''}`}>Deleted {fmtUKDate(it.deleted_at)}</span>
          {it.voided ? (
            <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/10 text-red-300 border border-red-500/30" title={`${it.voided_rows || 0} ledger row${it.voided_rows === 1 ? '' : 's'} voided — can't be restored`} data-testid={`inbox-voided-badge-${it.id}`}>Voided · {it.voided_rows || 0} row{it.voided_rows === 1 ? '' : 's'}</span>
          ) : (it.status === 'split' || it.split) ? (
            <span className="text-xs text-gray-500" title="Split invoices can't be restored — upload the invoice again if needed" data-testid={`inbox-split-norestore-${it.id}`}>Split · not restorable</span>
          ) : (
            <RestoreButton api={api} item={it} onRestored={onRestore} />
          )}
          <PurgeInvoiceButton api={api} item={it} onPurged={onPurge} />
        </>
      ) : (
        <>
          {open ? (
            <Button size="sm" onClick={() => onOpen(it)} className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold" data-testid={`inbox-process-${it.id}`}>
              {it.status === 'on_hold' ? 'Review' : 'Process'}
            </Button>
          ) : (
            <>
              <Button size="sm" variant="outline" onClick={() => onOpen(it)} className="border-white/20 text-white bg-white/5 hover:bg-white/10" data-testid={`inbox-view-${it.id}`}>View</Button>
              {it.status === 'posted' && (
                <Button size="sm" variant="outline" onClick={() => onOpen(it, { edit: true })} className="border-[#F5A623]/40 text-[#F5A623] bg-[#F5A623]/10 hover:bg-[#F5A623]/20" title="Edit this posted invoice — header, Billed to, CIS or lines" data-testid={`inbox-edit-${it.id}`}>
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                </Button>
              )}
            </>
          )}
          {api && it.status !== 'posted' && it.status !== 'split' && !it.split && (
            <DeleteInvoiceButton api={api} item={it} onDeleted={onDeleted} />
          )}
        </>
      )}
    </div>
  );
}
